import {
  BrainCircuit,
  Database,
  Eye,
  Plug,
  Shield,
  Sliders,
  Sparkles,
  User,
} from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { Badge } from "@/components/ui/badge";
import { AgentStatusBadge } from "@/features/agents/components/AgentStatusBadge";
import {
  useAgent,
  useAgentKbs,
  useAgentMcps,
} from "@/features/agents/hooks/useAgents";
import { useLlmModels } from "@/features/llms/hooks/useLlmModels";
import { useTeams } from "@/features/teams/hooks/useTeams";

interface Props {
  agentId: string;
  /** Nome do time dono, vindo da linha de aprovação (evita depender do
   * cache de times no cliente). */
  ownerTeamName?: string | null;
}

/**
 * Resumo somente-leitura do agente exibido para o aprovador na central de
 * Aprovações (publish/install). Dá contexto do que está sendo solicitado
 * (identidade, modelo, comportamento, KBs, ferramentas, visibilidade e um
 * trecho do prompt) sem precisar abrir o editor. Consome a API
 * (``AgentDetail`` + KBs + MCPs), diferente do ``PrePublishSummary`` que é
 * acoplado ao ``AgentDraft`` do editor.
 */
export function ApprovalAgentSummary({ agentId, ownerTeamName }: Props) {
  const { t } = useTranslation("agents");
  const agentQ = useAgent(agentId);
  const kbsQ = useAgentKbs(agentId);
  const mcpsQ = useAgentMcps(agentId);
  const { data: teams } = useTeams({ include_inactive: true });
  const { data: models } = useLlmModels({ include_inactive: true });

  const a = agentQ.data;
  const team = useMemo(
    () => teams?.find((tm) => tm.id === a?.owner_team_id) ?? null,
    [teams, a?.owner_team_id],
  );
  const model = useMemo(
    () => models?.find((m) => m.id === a?.llm_model_id) ?? null,
    [models, a?.llm_model_id],
  );

  if (agentQ.isLoading) return <LoadingState />;
  if (agentQ.isError || !a)
    return <ErrorState error={agentQ.error} onRetry={() => void agentQ.refetch()} />;

  const isExternal = a.agent_type === "external";
  const promptLines = (a.system_prompt ?? "").split("\n");

  return (
    <div className="space-y-3 rounded-xl border border-line bg-canvas p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-ink-950">
            {a.name}
          </h3>
          {a.description ? (
            <p className="line-clamp-2 text-xs text-ink-500">{a.description}</p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-1">
          <AgentStatusBadge status={a.status} />
          {isExternal ? (
            <Badge variant="outline">{t("external.badge")}</Badge>
          ) : null}
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <Block icon={User} title={t("publication.summary.identity")}>
          <KV
            k={t("publication.summary.fields.ownerTeam")}
            v={ownerTeamName ?? team?.name ?? "—"}
          />
        </Block>

        {!isExternal ? (
        <Block icon={BrainCircuit} title={t("publication.summary.model")}>
          <KV
            k={t("publication.summary.fields.llm")}
            v={model?.display_name ?? a.llm_model_id?.slice(0, 8) ?? "—"}
          />
          <KV
            k={t("publication.summary.fields.piiFilter")}
            v={
              a.pii_filter_enabled
                ? t("publication.summary.on")
                : t("publication.summary.off")
            }
          />
        </Block>
        ) : null}

        {!isExternal ? (
        <Block icon={Sliders} title={t("publication.summary.behavior")}>
          <div className="flex flex-wrap gap-1.5">
            {a.response_tone ? (
              <Badge variant="outline">
                {t("publication.summary.fields.tone")}: {a.response_tone}
              </Badge>
            ) : null}
            <Badge variant="outline">{a.response_language}</Badge>
            <Badge variant={a.memory_enabled ? "default" : "secondary"}>
              {t("publication.summary.fields.memory")}
            </Badge>
            <Badge variant={a.cite_sources ? "default" : "secondary"}>
              {t("publication.summary.fields.cite")}
            </Badge>
          </div>
        </Block>
        ) : null}

        <Block icon={Eye} title={t("publication.summary.visibility")}>
          <Badge variant="outline" className="inline-flex items-center gap-1">
            <Shield className="h-3 w-3" />
            {t(`publication.summary.visibilityMode.${a.visibility}`)}
          </Badge>
        </Block>

        {!isExternal ? (
        <Block icon={Database} title={t("publication.summary.knowledge")}>
          {kbsQ.data && kbsQ.data.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {kbsQ.data.map((kb) => (
                <Badge
                  key={kb.knowledge_base_id}
                  variant="outline"
                  className="gap-1"
                >
                  <Database className="h-3 w-3" />
                  {kb.knowledge_base_name}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-xs text-ink-500">
              {t("publication.summary.empty.kb")}
            </span>
          )}
        </Block>
        ) : null}

        {!isExternal ? (
        <Block icon={Plug} title={t("publication.summary.tools")}>
          <div className="flex flex-wrap gap-1.5">
            {a.web_search_enabled ? (
              <Badge variant="default">
                {t("publication.summary.fields.webSearch")}
              </Badge>
            ) : null}
            {a.reports_enabled ? (
              <Badge variant="default">
                {t("publication.summary.fields.reports")}
              </Badge>
            ) : null}
            {mcpsQ.data?.map((mcp) => (
              <Badge
                key={mcp.mcp_server_id}
                variant="outline"
                className="gap-1"
              >
                <Plug className="h-3 w-3" />
                {mcp.mcp_server_name}
                <span className="text-[10px] text-ink-500">
                  ({mcp.enabled_tools?.length ?? 0})
                </span>
              </Badge>
            ))}
            {!a.web_search_enabled &&
            !a.reports_enabled &&
            (!mcpsQ.data || mcpsQ.data.length === 0) ? (
              <span className="text-xs text-ink-500">
                {t("publication.summary.empty.mcp")}
              </span>
            ) : null}
          </div>
        </Block>
        ) : null}

        {isExternal ? (
          <Block
            icon={Plug}
            title={t("publication.summary.externalConnection")}
          >
            <p className="text-xs text-ink-500">
              {t("publication.summary.externalApproverHint")}
            </p>
          </Block>
        ) : null}
      </div>

      {!isExternal ? (
      <div className="border-t border-line pt-2">
        <h4 className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
          <Sparkles className="h-3.5 w-3.5 text-brand-500" />
          {t("publication.summary.systemPrompt")}
        </h4>
        {a.system_prompt ? (
          <pre className="mt-1 max-h-28 overflow-hidden whitespace-pre-wrap rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink-900">
            {promptLines.slice(0, 6).join("\n")}
            {promptLines.length > 6 ? "…" : ""}
          </pre>
        ) : (
          <p className="mt-1 text-xs text-ink-500">
            {t("publication.summary.empty.prompt")}
          </p>
        )}
      </div>
      ) : null}
    </div>
  );
}

function Block({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof User;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-line bg-surface p-2.5">
      <h5 className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
        <Icon className="h-3.5 w-3.5 text-brand-500" />
        {title}
      </h5>
      <div className="flex flex-col gap-1 text-xs text-ink-900">{children}</div>
    </div>
  );
}

function KV({ k, v }: { k: React.ReactNode; v: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2">
      <span className="text-[10px] uppercase tracking-wide text-ink-500">
        {k}
      </span>
      <span className="min-w-0 text-xs text-ink-900">{v}</span>
    </div>
  );
}
